import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, AlertCircle, ArrowLeft, ShieldCheck } from "lucide-react";
import { supabase } from "../../lib/supabase";

const PasswordResetEmailScreen = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState("");

  const validateEmail = (val) => {
    if (!val?.trim()) return "Email address is required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(val?.trim())) return "Please enter a valid email address.";
    return "";
  };

  const handleChange = (e) => {
    setEmail(e?.target?.value);
    if (error) setError("");
    if (apiError) setApiError("");
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const validationError = validateEmail(email);
    if (validationError) {
      setError(validationError);
      return;
    }
    setIsLoading(true);
    setApiError("");
    try {
      const { data, error: fnError } = await supabase?.functions?.invoke("send-email-otp", {
        body: { email: email?.trim(), action: "send" },
      });
      if (fnError || data?.error) {
        setApiError(fnError?.message || data?.error || "Failed to send reset code. Please try again.");
      } else {
        navigate("/password-reset-otp-verification-screen", {
          state: { email: email?.trim() },
        });
      }
    } catch (err) {
      setApiError("Network error. Please check your connection and try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="w-full px-6 py-4 flex items-center justify-between border-b border-border bg-white">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
            <span className="text-white font-bold text-sm">LPS</span>
          </div>
          <span className="font-semibold text-foreground text-sm hidden sm:block">Volunteer Portal</span>
        </Link>
        <Link
          to="/login-screen"
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Login
        </Link>
      </header>

      {/* Main */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-sm border border-border p-8">
            {/* Icon + Heading */}
            <div className="flex flex-col items-center mb-8">
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <ShieldCheck className="w-7 h-7 text-primary" />
              </div>
              <h1
                className="text-2xl font-bold text-foreground mb-1.5 text-center"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                Reset your password
              </h1>
              <p className="text-sm text-muted-foreground text-center">
                Enter your registered email address and we'll send you a verification code to reset your password.
              </p>
            </div>

            {/* Step indicator */}
            <div className="flex items-center gap-2 mb-6">
              <div className="flex items-center gap-1.5">
                <div className="w-6 h-6 rounded-full bg-primary text-white text-xs font-bold flex items-center justify-center">1</div>
                <span className="text-xs font-medium text-primary">Email</span>
              </div>
              <div className="flex-1 h-px bg-border" />
              <div className="flex items-center gap-1.5">
                <div className="w-6 h-6 rounded-full bg-muted text-muted-foreground text-xs font-bold flex items-center justify-center">2</div>
                <span className="text-xs text-muted-foreground">Verify</span>
              </div>
              <div className="flex-1 h-px bg-border" />
              <div className="flex items-center gap-1.5">
                <div className="w-6 h-6 rounded-full bg-muted text-muted-foreground text-xs font-bold flex items-center justify-center">3</div>
                <span className="text-xs text-muted-foreground">New Password</span>
              </div>
            </div>

            {/* API Error */}
            {apiError && (
              <div className="flex items-start gap-3 p-4 rounded-lg bg-red-50 border border-red-200 mb-5">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{apiError}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate className="space-y-5">
              {/* Email Field */}
              <div className="space-y-1.5">
                <label htmlFor="reset-email" className="block text-sm font-medium text-gray-700">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="w-4 h-4 text-gray-400" />
                  </div>
                  <input
                    id="reset-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={handleChange}
                    placeholder="you@example.com"
                    className={`w-full pl-10 pr-4 py-3 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-0 ${
                      error
                        ? "border-red-400 focus:ring-red-300 bg-red-50" :"border-gray-200 focus:ring-primary/30 focus:border-primary bg-white"
                    }`}
                  />
                </div>
                {error && (
                  <p className="flex items-center gap-1.5 text-xs text-red-600">
                    <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                    {error}
                  </p>
                )}
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 py-3 px-6 rounded-lg bg-primary text-white font-semibold text-sm transition-all hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Sending code…
                  </>
                ) : (
                  "Send Reset Code"
                )}
              </button>
            </form>

            {/* Security note */}
            <div className="mt-6 p-3 bg-muted/40 rounded-lg border border-border">
              <p className="text-xs text-muted-foreground text-center">
                🔒 For your security, the reset code expires in 10 minutes.
              </p>
            </div>
          </div>

          <p className="text-center text-sm text-muted-foreground mt-6">
            Remember your password?{" "}
            <Link to="/login-screen" className="text-primary font-medium hover:text-primary/80 transition-colors">
              Sign in
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
};

export default PasswordResetEmailScreen;
